<template>
  <section>
    <h5 :class="{ focus }" :style="{ color: visualSettings.sidebarHeadingLabelColor }">
      <i>{{visualSettings.directChannelLabel}}</i>
      <span tabindex="0">
        <input 
          type="text"
          ref="searchInput"
          v-model="query"
          aria-label="Search"
          :placeholder="focus ? 'Search...' : ''"
          @focus="openSearch"
          @blur="onBlur"
        />
        <fa-icon 
          :icon="['fas', 'search']" 
          size="2x"
          @click="openSearch"
        />
      </span>
    </h5>

    <Notification
      v-if="!activeUsers.length && !recentUsers.length"
      message="Loading Users..."
      :icon="['fas', 'spinner']"
      type="info"
    />

    <ChannelRow
      v-for="user in filteredRecentUsers" :key="user.uuid"
      :channel-id="user.channelId"
      :name="user.name"
      :image="user.avatar"
      :notification-count="(channelById(user.channelId) || {}).notificationCount"
      :active="user.active"
      :sponsor="user.sponsor"
      :enabled="visualSettings.userDirectMessaging"
      :show-status="true"
      :you="activeUser && activeUser.uuid === user.uuid"
    />

    <hr v-if="recentUsers.length" />

    <virtual-list
      class="list"
      :data-key="'uuid'"
      :data-sources="filteredActiveUsers"
      :data-component="channelRowVirtualItem"
      :estimate-size="39"
      :keeps="25"
    />
  </section>
</template>

<script>
import { mapGetters, mapMutations, mapState } from 'vuex';
import VirtualList from 'vue-virtual-scroll-list';

import Notification from '@/components/Notification';
import ChannelRow from '@/components/ChannelRow';
import ChannelRowVirtualItem from '@/components/ChannelRowVirtualItem';

export default {
  name: 'Users',
  components: {
    Notification,
    ChannelRow,
    'virtual-list': VirtualList
  },
  data() {
    return {
      query: '',
      focus: false,
      channelRowVirtualItem: ChannelRowVirtualItem,
      userSortTimeout: undefined
    }
  },
  computed: {
    ...mapGetters([
      'channelById',
      'visualSettings'
    ]),
    ...mapState([
      'users',
      'activeUser',
      'activeUsers',
      'recentUsers'
    ]),
    filteredRecentUsers() {
      return this.query && this.focus && this.recentUsers.filter((user) => {
        return (
          user.name.toLowerCase().includes(this.query.toLowerCase()) || 
          ('sponsor'.includes(this.query) && user.sponsor) ||
          ('verified'.includes(this.query) && user.sponsor)
        );
      }) || this.recentUsers;
    },
    filteredActiveUsers() {
      return this.query && this.focus && this.activeUsers.filter((user) => {
        return (
          user.name.toLowerCase().includes(this.query.toLowerCase()) || 
          ('sponsor'.includes(this.query) && user.sponsor) ||
          ('verified'.includes(this.query) && user.sponsor)
        );
      }) || this.activeUsers;
    }
  },
  methods: {
    ...mapMutations([
      'sortUsers'
    ]),
    updateSort() {
      this.sortUsers();
      const delay = this.users.length;
      this.userSortTimeout = setTimeout(this.updateSort, delay < 2500 ? 2500 : delay);
    },
    openSearch(e) {
      e.stopPropagation();
      e.preventDefault();
      this.focus = true;
      this.$refs.searchInput.focus();
    },
    onBlur() {
      setTimeout(() => {
        this.focus = false;
      }, 500);
    }
  },
  created() {
    this.updateSort();
  },
  destroy() {
    clearTimeout(this.userSortTimeout);
  }
}
</script>

<style scoped lang="scss">
section {
  position: absolute;
  display: flex;
  flex-flow: column;
  align-items: stretch;
  height: 100%;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;

  > * {
    flex: 0 1 auto;
  }

  .list {
    flex: 1 1 auto;
    overflow-y: auto;
  }
}

h5 {
  position: relative;
  margin: 0 0 6px 0;
  font-size: 13.6px;
  line-height: 1.1;
  color: rgba(47,49,56,0.6);

  i {
    transition: color 250ms cubic-bezier(0.230, 1.000, 0.320, 1.000);
    font-style: normal;
  }

  span {
    position: absolute;
    top: -5px;
    right: 0;
    width: 24px;

    transition: width 250ms cubic-bezier(0.230, 1.000, 0.320, 1.000);

    svg {
      position: absolute;
      font-size: 14px;
      top: 5px;
      right: 5px;
      cursor: pointer;
    }

    input {
      font-size: 11px;
      line-height: 1;
      background: rgba(0,0,0,0);
      border: 0;
      border-radius: 4px;
      color: rgba(255,255,255,0);
      width: 100%;
      box-sizing: border-box;
      padding: 5px;
      outline: none;

      transition: color 250ms cubic-bezier(0.230, 1.000, 0.320, 1.000),
        background 250ms cubic-bezier(0.230, 1.000, 0.320, 1.000),
        padding 250ms cubic-bezier(0.230, 1.000, 0.320, 1.000);
    }
  }

  &.focus {
    
    i {
      color: rgba(0,0,0,0);
    }

    span {
      width: 100%;

      input {
        padding-right: 24px;
        background: rgba(0,0,0,0.25);
        color: rgba(255,255,255,0.75);
      }
    }
  }
}

hr {
  margin: 10px 0;
  border: 0;
  height: 1px;
  background: rgba(47,49,56,0.1);
}
</style>
